"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CircleCheck, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  title: "Compressores Scroll & Parafuso",
  description: "Utilizamos compressores de alta eficiência energética (Bitzer, Danfoss, Copeland) com controle de capacidade, garantindo estabilidade térmica e menor consumo em cargas parciais.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/compressor_detail.png",
  alt: "Detalhe de compressor de alta eficiência"
}, {
  title: "Revestimento Anticorrosivo",
  description: "Serpentinas e aletas com tratamento Gold Fin ou Blue Fin, essenciais para ambientes salinos ou industriais agressivos, prolongando a vida útil do equipamento em até 3x.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/coating_protection.png",
  alt: "Proteção anticorrosiva em aletas"
}, {
  title: "Controladores PLC Dedicados",
  description: "Microprocessadores industriais com lógica PID avançada para controle preciso de temperatura (±0,5°C) e umidade, com protocolos de comunicação Modbus/BACnet nativos.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/plc_controller_screen.png",
  alt: "Interface de controlador PLC industrial"
}, {
  title: "Estrutura em Aço Galvanizado",
  description: "Gabinetes robustos fabricados em chapa de aço galvanizado com pintura eletrostática a pó, projetados para suportar vibrações e garantir estanqueidade (NEMA 4 / IP54).",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/steel_structure_frame.png",
  alt: "Estrutura metálica reforçada"
}, {
  title: "Ventiladores EC Eletrônicos",
  description: "Ventiladores com tecnologia EC (Electronically Commutated) que ajustam a rotação conforme a demanda real, reduzindo ruído e consumo elétrico drasticamente.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/ec_fan_assembly.png",
  alt: "Ventilador eletrônico EC"
}, {
  title: "Válvulas de Expansão Eletrônica",
  description: "Controle preciso do superaquecimento através de válvulas eletrônicas, otimizando a performance do evaporador e protegendo o compressor contra retorno de líquido.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/electronic_expansion_valve.png",
  alt: "Válvula de expansão eletrônica"
}];
export default function DiferenciaisConstrutivos() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="diferenciais-construtivos" ref={ref} className="relative w-full py-20 bg-background overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
        <svg className="w-full h-full" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent-foreground text-sm font-medium mb-4 border border-accent/30">
            <CircleCheck className="w-4 h-4" />
            <span>Engenharia de Precisão</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
            Diferenciais Construtivos
          </h2>
          <p className="text-lg text-muted-foreground">
            Cada componente é selecionado rigorosamente para garantir a máxima confiabilidade em operações críticas, onde falhas não são uma opção.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {features.map((feature, index) => <div key={index} data-index={index} className={`group flex flex-col items-center text-center transition-all duration-700 delay-[${index * 100}ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              {}
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-primary/5 blur-xl group-hover:bg-primary/10 transition-colors duration-500" />
                <div className="relative w-40 h-40 rounded-full overflow-hidden border-4 border-background shadow-lg ring-1 ring-border group-hover:ring-primary/50 transition-all duration-300">
                  <img src={feature.image} alt={feature.alt} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />
                </div>
                {}
                <div className="absolute -bottom-2 -right-2 bg-card border border-border p-1.5 rounded-full shadow-sm">
                   <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                </div>
              </div>

              <h3 className="text-xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
                {feature.title}
              </h3>
              
              <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
                {feature.description}
              </p>
            </div>)}
        </div>

        <div className={`mt-16 text-center transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Button asChild size="lg" className="rounded-full px-8">
            <Link to="/EquipmentPage">
              Ver Linha de Equipamentos
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}